/**
 * Phase N — /privacy · Privacy Policy (draft, pending legal review).
 *
 * TODO: confirm exact legal entity name.
 * TODO: pending attorney review before public launch.
 */
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import LegalReviewBanner from "@/components/LegalReviewBanner";

const LAST_UPDATED = "February 2026";

function LegalSection({ title, id, testId, children }) {
  return (
    <section id={id} data-testid={testId} className="mt-10">
      <h2 className="font-serif text-2xl text-ivy tracking-tight">{title}</h2>
      <div className="mt-4 text-ink-soft leading-relaxed space-y-3 text-[15px]">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <Layout>
      <SEO
        title="Privacy Policy | Beyond Valencia"
        description="Beyond Valencia privacy policy — what data we collect, why we collect it, how we store it, and how you can exercise your privacy rights."
        canonical="/privacy"
        noindex={false}
      />
      <LegalReviewBanner />

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-20" data-testid="privacy-page">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Legal · Draft</div>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-ivy tracking-tight" data-testid="privacy-h1">
          Privacy Policy
        </h1>
        <p className="mt-3 text-sm text-stone">Last updated: {LAST_UPDATED}</p>

        <p className="mt-8 text-ink-soft leading-relaxed">
          {/* TODO: confirm exact legal entity name */}
          Beyond Valencia LLC (&ldquo;Beyond Valencia&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;)
          respects your privacy. This policy describes what personal information we collect, why we collect it,
          how we use and share it, and the choices you have.
        </p>

        <LegalSection title="1. Information we collect" id="collection" testId="privacy-collection">
          <p>We collect information you provide directly, information generated by your use of our services, and information from third parties that authenticate you or process payments on our behalf.</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Account information</strong> — name, email address, and profile image from your Google sign-in.</li>
            <li><strong>Transfer profile</strong> — community college, target university, intended major, coursework history, GPA, and any transcripts you upload to our diagnostic tools.</li>
            <li><strong>Payment information</strong> — processed directly by Stripe; we never see full card numbers. We store a transaction identifier and status.</li>
            <li><strong>Usage data</strong> — pages viewed, tools used, and interactions with our platform. Recorded for product analytics and abuse prevention.</li>
          </ul>
        </LegalSection>

        <LegalSection title="2. How we use your information" id="use" testId="privacy-use">
          <ul className="list-disc pl-5 space-y-2">
            <li>To deliver the diagnostics, requirement graphs, and adviser services you request.</li>
            <li>To process payments and provide receipts.</li>
            <li>To send you transactional communications (account, receipts, adviser follow-ups) and, if you opt in, occasional educational content.</li>
            <li>To improve our platform through aggregated analytics.</li>
            <li>To comply with legal obligations and enforce our terms of service.</li>
          </ul>
        </LegalSection>

        <LegalSection title="3. How we share information" id="sharing" testId="privacy-sharing">
          <p>We share personal information only where necessary to run the service or where required by law:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Service providers</strong> — Stripe (payments), Emergent (authentication and object storage), Resend (transactional email), and Google (sign-in).</li>
            <li><strong>Ivy League Consulting</strong> — when your target list makes you a better fit for our partner&apos;s elite-transfer service, we may route your inquiry to them with your consent.</li>
            <li><strong>Legal compliance</strong> — where required by law, court order, or valid legal request.</li>
          </ul>
          <p>We do not sell your personal information.</p>
        </LegalSection>

        <LegalSection title="4. Data retention" id="retention" testId="privacy-retention">
          <p>We retain personal information for as long as you have an active account and for the additional period required to comply with legal obligations, resolve disputes, and enforce our agreements. You may request deletion at any time (see Section 6).</p>
        </LegalSection>

        <LegalSection title="5. Security" id="security" testId="privacy-security">
          <p>We use industry-standard controls to protect your data in transit and at rest — including encrypted connections, access controls, and audit logging. No online service can guarantee absolute security, and we do not represent otherwise.</p>
        </LegalSection>

        <LegalSection title="6. Your rights" id="rights" testId="privacy-rights">
          <p>Depending on your jurisdiction, you may have the right to access, correct, delete, or export your personal information, and to object to or restrict certain processing. To exercise any of these rights, contact us at{" "}
            {/* TODO: confirm mailbox routing */}
            <a href="mailto:hello@beyondvalencia.com" className="text-ivy hover:text-brass underline" data-testid="privacy-contact-email">
              hello@beyondvalencia.com
            </a>
            . We will respond within a reasonable time and in accordance with applicable law.
          </p>
        </LegalSection>

        <LegalSection title="7. Children" id="children" testId="privacy-children">
          <p>Our services are intended for prospective and current college students aged 13 and older. If you are under 18, you must have permission from a parent or legal guardian to use our paid services. We do not knowingly collect information from children under 13.</p>
        </LegalSection>

        <LegalSection title="8. Changes to this policy" id="changes" testId="privacy-changes">
          <p>We may update this policy from time to time. When we do, we will update the &ldquo;last updated&rdquo; date at the top of this page and, for material changes, provide additional notice.</p>
        </LegalSection>

        <LegalSection title="9. Contact" id="contact" testId="privacy-contact">
          <p>
            {/* TODO: confirm exact legal entity name and mailing address before publishing */}
            Beyond Valencia LLC · Orlando, Florida ·{" "}
            <a href="mailto:hello@beyondvalencia.com" className="text-ivy hover:text-brass underline">
              hello@beyondvalencia.com
            </a>
          </p>
        </LegalSection>

        <p className="mt-16 text-xs text-stone italic" data-testid="privacy-draft-disclaimer">
          This document is a draft pending legal review. It is not a substitute for advice from qualified counsel.
        </p>
      </div>
    </Layout>
  );
}
